import React from 'react';
import {StyleSheet, Text, View} from 'react-native';
import {useTheme} from '../HelperClasses/ThemeContext';
import {useLocale} from '../Localization/LocaleContext';

const CouponEmptyState = ({title, subtitle}) => {
  const {isDark} = useTheme();
  const {translate} = useLocale();
  const palette = isDark
    ? {
        shell: '#131816',
        shellBorder: '#37433f',
        title: '#ffffff',
        card: '#202824',
        cardBorder: '#34423b',
        icon: '#2c6853',
        iconText: '#b7f2d7',
        field: '#0e1210',
        fieldBorder: '#52635b',
        muted: '#9ca9a3',
        remove: '#254a3c',
        removeText: '#a8edd0',
        border: '#303b36',
        grand: '#ffffff',
        subtitle: '#9ca9a3',
      }
    : {
        shell: '#ffffff',
        shellBorder: '#dbe8e1',
        title: '#15221b',
        card: '#f4faf7',
        cardBorder: '#d7e8df',
        icon: '#d9f3e6',
        iconText: '#27835f',
        field: '#ffffff',
        fieldBorder: '#cbded3',
        muted: '#6c7b73',
        remove: '#e8f7ef',
        removeText: '#287a58',
        border: '#e0ebe5',
        grand: '#15221b',
        subtitle: '#6c7b73',
      };

  return (
    <View style={styles.wrapper}>
      <View style={styles.contentColumn}>
      <View style={[styles.previewShell, {backgroundColor: palette.shell, borderColor: palette.shellBorder}]}>
        <View style={styles.previewHeader}>
          <View>
            <Text style={styles.eyebrow}>{translate('coupons.preview.customer_checkout', 'CUSTOMER CHECKOUT')}</Text>
            <Text style={[styles.previewTitle, {color: palette.title}]}>{translate('coupons.preview.title', 'Coupons in action')}</Text>
          </View>
          <View style={styles.discountBadge}>
            <Text style={styles.discountBadgeText}>−10%</Text>
          </View>
        </View>

        <View style={[styles.orderCard, {backgroundColor: palette.card, borderColor: palette.cardBorder}]}>
          <View style={[styles.productIcon, {backgroundColor: palette.icon}]}>
            <Text style={[styles.productIconText, {color: palette.iconText}]}>✦</Text>
          </View>
          <View style={styles.productDetails}>
            <Text style={[styles.productName, {color: palette.title}]}>{translate('coupons.preview.featured_product', 'Your featured product')}</Text>
            <Text style={[styles.productMeta, {color: palette.muted}]}>{translate('coupons.preview.product_meta', '1 item · Standard delivery')}</Text>
          </View>
          <Text style={[styles.productPrice, {color: palette.title}]}>$230</Text>
        </View>

        <View style={styles.couponRow}>
          <View style={[styles.couponField, {backgroundColor: palette.field, borderColor: palette.fieldBorder}]}>
            <Text style={[styles.couponCode, {color: palette.title}]}>WELCOME10</Text>
            <Text style={styles.couponHint}>{translate('coupons.preview.coupon_applied', 'Coupon applied')}</Text>
          </View>
          <View style={[styles.removeButton, {backgroundColor: palette.remove}]}>
            <Text style={[styles.removeText, {color: palette.removeText}]}>{translate('coupons.preview.remove', 'Remove')}</Text>
          </View>
        </View>

        <View style={styles.successMessage}>
          <Text style={styles.successIcon}>✓</Text>
          <Text style={styles.successText}>{translate('coupons.preview.discount_applied', 'Discount applied to the order')}</Text>
        </View>

        <View style={[styles.totalBlock, {borderTopColor: palette.border}]}>
          <View style={styles.totalRow}>
            <Text style={[styles.mutedLabel, {color: palette.muted}]}>{translate('coupons.preview.subtotal', 'Subtotal')}</Text>
            <Text style={[styles.mutedValue, {color: palette.muted}]}>$230</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.discountLabel}>{translate('coupons.preview.coupon_label', 'Coupon · WELCOME10')}</Text>
            <Text style={styles.discountValue}>−$10</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={[styles.grandLabel, {color: palette.grand}]}>{translate('coupons.preview.grand_total', 'Grand total')}</Text>
            <Text style={[styles.grandValue, {color: palette.grand}]}>$220</Text>
          </View>
        </View>

        <View style={styles.checkoutButton}>
          <Text style={styles.checkoutText}>{translate('coupons.preview.checkout', 'CHECKOUT')}</Text>
        </View>
      </View>
      <Text style={[styles.title, {color: palette.title}]}>{title}</Text>
      {!!subtitle && <Text style={[styles.subtitle, {color: palette.subtitle}]}>{subtitle}</Text>}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    minWidth: 0,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingVertical: 28,
    paddingHorizontal: 16,
    boxSizing: 'border-box',
  },
  contentColumn: {
    width: '100%',
    maxWidth: 640,
    alignItems: 'stretch',
    alignSelf: 'center',
  },
  previewShell: {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    padding: 22,
    borderRadius: 18,
    backgroundColor: '#131816',
    borderWidth: 1,
    borderColor: '#37433f',
    shadowColor: '#000000',
    shadowOpacity: 0.25,
    shadowRadius: 18,
    shadowOffset: {width: 0, height: 8},
  },
  previewHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  eyebrow: {
    color: '#67d3a5',
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 2,
  },
  previewTitle: {
    color: '#ffffff',
    fontSize: 22,
    fontWeight: '700',
    marginTop: 5,
  },
  discountBadge: {
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 999,
    backgroundColor: '#183d31',
  },
  discountBadgeText: {
    color: '#7ce0b5',
    fontSize: 12,
    fontWeight: '700',
  },
  orderCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 13,
    borderRadius: 12,
    backgroundColor: '#202824',
    borderWidth: 1,
    borderColor: '#34423b',
  },
  productIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#2c6853',
  },
  productIconText: {
    color: '#b7f2d7',
    fontSize: 24,
  },
  productDetails: {
    flex: 1,
    paddingHorizontal: 12,
  },
  productName: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '700',
  },
  productMeta: {
    color: '#9ca9a3',
    fontSize: 11,
    marginTop: 4,
  },
  productPrice: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '700',
  },
  couponRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    marginTop: 14,
    gap: 8,
  },
  couponField: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 9,
    borderRadius: 9,
    backgroundColor: '#0e1210',
    borderWidth: 1,
    borderColor: '#52635b',
  },
  couponCode: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 1,
  },
  couponHint: {
    color: '#67d3a5',
    fontSize: 10,
    marginTop: 3,
  },
  removeButton: {
    minWidth: 78,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 9,
    backgroundColor: '#254a3c',
  },
  removeText: {
    color: '#a8edd0',
    fontSize: 12,
    fontWeight: '700',
  },
  successMessage: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
  },
  successIcon: {
    width: 18,
    height: 18,
    borderRadius: 9,
    textAlign: 'center',
    color: '#0e1b15',
    backgroundColor: '#67d3a5',
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 18,
  },
  successText: {
    color: '#67d3a5',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 7,
  },
  totalBlock: {
    marginTop: 18,
    paddingTop: 14,
    borderTopWidth: 1,
    borderTopColor: '#303b36',
    gap: 9,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mutedLabel: {color: '#8f9a94', fontSize: 12},
  mutedValue: {color: '#d9e1dc', fontSize: 12},
  discountLabel: {color: '#67d3a5', fontSize: 12},
  discountValue: {color: '#67d3a5', fontSize: 12, fontWeight: '700'},
  grandLabel: {color: '#ffffff', fontSize: 16, fontWeight: '700'},
  grandValue: {color: '#ffffff', fontSize: 18, fontWeight: '700'},
  checkoutButton: {
    marginTop: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 13,
    borderRadius: 10,
    backgroundColor: '#67d3a5',
  },
  checkoutText: {
    color: '#0e1b15',
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 1.5,
  },
  title: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
    marginTop: 14,
    width: '100%',
  },
  subtitle: {
    color: '#9ca9a3',
    fontSize: 12,
    lineHeight: 18,
    textAlign: 'center',
    marginTop: 6,
    width: '100%',
  },
});

export default CouponEmptyState;
